import { useState, useCallback, useRef } from 'react';

export const useEditorState = (initialContent = '') => {
  const [content, setContent] = useState(initialContent);
  const [selection, setSelection] = useState(null);
  const [history, setHistory] = useState([initialContent]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const quillRef = useRef(null);

  const updateContent = useCallback((newContent) => {
    setContent(newContent);

    // Update history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setContent(history[newIndex]);
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setContent(history[newIndex]);
    }
  }, [history, historyIndex]);

  const getEditor = useCallback(() => {
    return quillRef.current?.getEditor();
  }, []);

  const getSelection = useCallback(() => {
    const editor = getEditor();
    if (!editor) return null;

    const range = editor.getSelection();
    if (!range) return null;

    return {
      index: range.index,
      length: range.length,
      text: editor.getText(range.index, range.length),
      format: editor.getFormat(range.index, range.length),
    };
  }, [getEditor]);

  return {
    content,
    setContent: updateContent,
    selection,
    setSelection,
    history,
    historyIndex,
    undo,
    redo,
    quillRef,
    getEditor,
    getSelection,
  };
};
